import { FieldPath, Fields } from '../../@types/field-path.d';
import type { AssetURLs } from '../attachments';
import type { ConflictItem, Item } from '../items';

export type KitStatus =
	| 'available'
	| 'checkedout'
	| 'await_checkout'
	| 'in_transit'
	| 'maintenance'
	| 'repair'
	| 'inspection'
	| 'expired'
	| 'in_custody'
	| 'empty'
	| 'incomplete';

export type KitAvailabilityStatus =
	| 'available'
	| 'available_partially'
	| 'unavailable_status'
	| 'unavailable_flag'
	| 'unavailable_allow'
	| 'unavailable_other'
	| 'unavailable_bulk';

export type RestrictionLevel = 'full' | 'partial' | 'none';

export interface Kit {
	_id: string;
	name: string;
	status: KitStatus;
	/**
	 * @deprecated use itemQuantities
	 * @see itemQuantities
	 */
	items: Item[];
	cover: string | null;
	cover_url?: AssetURLs;
	created: string;
	modified: string;
	description: string;
	itemSummary: string;
	codes: string[];
	barcodes: string[];
	rfidcodes: string[];
	canReserve: KitAvailabilityStatus;
	canOrder: KitAvailabilityStatus;
	canCustody: KitAvailabilityStatus;
	allowReserve: boolean;
	allowOrder: boolean;
	allowCustody: boolean;
	fields: Record<string, string>;
	itemQuantities: Record<string, number>;
	locked?: boolean;
	isRestricted: RestrictionLevel;
}

export enum KitConflict {
	AVAILABLE = 'AVAILABLE',
	UNAVAILABLE = 'UNAVAILABLE',
	RESTRICTED = 'RESTRICTED',
	PARTIALLY_AVAILABLE = 'PARTIALLY_AVAILABLE',
	PARTIALLY_RESTRICTED = 'PARTIALLY_RESTRICTED',
	AT_WRONG_LOCATION = 'WRONG_LOCATION',
	CHECKED_OUT = 'CHECKED_OUT',
	ASSIGNED = 'IN_CUSTODY',
	CHECKING_OUT = 'CHECKING_OUT',
	FLAGGED = 'FLAGGED',
	RESERVED = 'RESERVED',
	CANNOT_CHECKOUT = 'CANNOT_CHECKOUT',
	CANNOT_RESERVE = 'CANNOT_RESERVE',
	RETIRED = 'RETIRED',
	INCOMPLETE = 'INCOMPLETE',
	PARTIALLY_INCOMPLETE = 'PARTIALLY_INCOMPLETE',
	BUFFER_TIME = 'BUFFER_TIME',
}

export type ConflictKit = Omit<ConflictItem, 'kind'> & {
	locked: boolean;
};

export type CustomFieldKey = `fields.${string}`;
export type SystemField =
	| 'name'
	| 'created'
	| 'modified'
	| 'description'
	| 'items'
	| 'codes'
	| 'barcodes'
	| 'rfidcodes'
	| 'isRestricted'
	| 'locked';
export type Field = SystemField | CustomFieldKey;

export const KIT_SYSTEM_FIELDS = [
	{
		id: 'name',
		name: 'Name',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
	},
	{
		id: 'created',
		name: 'Created',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'modified',
		name: 'Modified',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'description',
		name: 'Description',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		allowedComparators: undefined,
	},
	{
		id: 'items',
		name: 'Items',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'codes',
		name: 'QR',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		allowedComparators: ['contains', 'starts_with', 'ends_with', 'empty', 'not_empty'],
	},
	{
		id: 'barcodes',
		name: 'Barcode',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		allowedComparators: ['contains', 'starts_with', 'ends_with', 'empty', 'not_empty'],
	},
	{
		id: 'rfidcodes',
		name: 'RFID',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		allowedComparators: ['contains', 'starts_with', 'ends_with', 'empty', 'not_empty'],
	},
	{
		id: 'isRestricted',
		name: 'Access',
		kind: 'boolean',
		editor: 'boolean',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'locked',
		name: 'Kit locked',
		kind: 'boolean',
		editor: 'boolean',
		unit: null,
		excludeFromFilters: true,
	},
] as const;

export type KitQueryField = FieldPath<Kit> | keyof Kit | CustomFieldKey;
/** Empty `fields: []` infers `never`; treat as full field set (matches DEFAULT_KIT_FIELDS merge). */
export type KitQueryData<F extends KitQueryField = KitQueryField> = Fields<
	Kit,
	Extract<[F] extends [never] ? KitQueryField : F, FieldPath<Kit> | keyof Kit>
>;
